/**
 * OutlineOffsetController — the Offset *shape kind* for the leather Draw tools
 * (#972, epic #781). Pressing on an existing outline's edge and dragging lays a
 * parallel outline; releasing commits it.
 *
 * A tool OWNS a controller instance and DRIVES it by composition (it is NOT a
 * base class), the same arrangement `OffsetController` / `OutlineEditController`
 * use. Everything tool-specific — which outlines can be offset, which sides are
 * allowed, and what a release commits — is injected through
 * `OutlineOffsetHost`, so ShapeTool (leather: a new part, inset or outset) and
 * CutoutTool (a new cutout, inset only) share one gesture rather than each
 * growing its own copy.
 *
 * The gesture deliberately mirrors the construction Offset sub-tool and the
 * Stitch tool's Offset seam kind (#148 / #768) down to the cursor treatment: a
 * plain pointer rather than a crosshair (this grabs an edge, it doesn't place a
 * point), plus the perpendicular-arrow hover affordance over an offsettable
 * edge, then a dashed preview and a labelled distance ray during the drag.
 *
 * All geometry is reused, not reimplemented: `nearestOutlineEdge` /
 * `distanceToEdge` / `nearestPointOnEdge` / `offsetHoverGeometry` from
 * `stitchOffset.ts` (extracted for #768) and `offsetOutlinePathData` from
 * `pieceOffset.ts` over the construction offset engine.
 */
import paper from "paper";
import type { Scene } from "../canvas/Scene";
import { isVisible, type Project } from "../model/Project";
import {
  distanceToEdge,
  nearestOutlineEdge,
  nearestPointOnEdge,
  offsetHoverGeometry,
} from "../model/stitchOffset";
import { offsetOutlinePathData } from "../model/pieceOffset";
import { setCanvasCursor } from "../canvas/canvasCursor";
import { cssVar } from "../colors";
import { currentPointerKind, hitRadiusFor } from "../canvas/pointerPrecision";

/** Dashed stroke for the live preview. The same accent pair the distance ray
 *  and the other leather drag previews use — light over the bare mat, dark over
 *  leather, where the pale fill would wash the light one out. Read through
 *  functions rather than captured at module load so a light/dark theme switch
 *  is picked up. */
const PREVIEW_STROKE = () => cssVar("--canvas-accent");
const PREVIEW_STROKE_OVER_LEATHER = () => cssVar("--canvas-accent-leather");

/** How near (screen px → mm per zoom) the cursor must be to an edge for the
 *  gesture to engage. Matches the Stitch tool's offset tolerance. */
const EDGE_TOL_PX = 6;

/** Minimum distance a drag must reach before release commits — a click with no
 *  meaningful drag is a no-op, matching `OffsetController`. Bypassed when a
 *  preset distance is typed, since then a click alone is a complete
 *  instruction. */
const MIN_DISTANCE_MM = 0.5;

/** How far the pointer must leave the press point before it's treated as
 *  choosing a side. Below this it's still sitting on the edge itself. */
const SIDE_SLOP_MM = 0.5;

/** One outline the gesture may offset FROM, plus which sides it may go to. */
export interface OffsetCandidate {
  /** The part this source belongs to (the owner a committed cutout lands in). */
  pieceId: string;
  /** Set when the source is a cutout rather than the part outline itself. */
  cutoutId?: string;
  pathData: string;
  /** Whether the result may sit inside the source outline (an inset). */
  allowInside: boolean;
  /** Whether it may sit outside (an outset). */
  allowOutside: boolean;
}

/**
 * Outlines the Offset kind can start FROM in the leather Draw tools: every
 * visible, non-linked part outline plus every visible, non-linked cutout.
 *
 * ShapeTool and CutoutTool scan exactly this set — they differ only in which
 * SIDES are legal from each source — so it lives here rather than being written
 * out twice. The properties panel uses it too, to grey the Offset tile out when
 * there is nothing to offset from; sharing the rule is what keeps the tile's
 * enabled state and the gesture's actual behaviour from disagreeing.
 *
 * Linked (construction-derived) outlines are excluded because their geometry
 * re-derives on every commit, so an offset taken from one would be a snapshot
 * of a moving target — which also means a project whose only part is traced
 * correctly reports "nothing to offset from".
 */
export function offsetSourceOutlines(
  project: Project,
): Array<{ pieceId: string; cutoutId?: string; pathData: string }> {
  const out: Array<{ pieceId: string; cutoutId?: string; pathData: string }> =
    [];
  for (const piece of project.pieces) {
    if (!isVisible(piece)) continue;
    if (!piece.constructionOutline) {
      out.push({ pieceId: piece.id, pathData: piece.shapePathData });
    }
    for (const cut of piece.cutouts ?? []) {
      if (!isVisible(cut) || cut.constructionOutline) continue;
      out.push({
        pieceId: piece.id,
        cutoutId: cut.id,
        pathData: cut.pathData,
      });
    }
  }
  return out;
}

export interface OutlineOffsetHost {
  getScope(): paper.PaperScope;
  getScene(): Scene;
  /** Outlines the gesture may offset from, in the tool's current mode. */
  offsetCandidates(): OffsetCandidate[];
  /** Commit the finished outline. The controller has already validated that
   *  the geometry is a usable closed outline on an allowed side. */
  commitOffset(source: OffsetCandidate, pathData: string): void;
  /** Release produced no usable geometry — the inset went past the point where
   *  the shape collapses. The tool surfaces this however it prefers. */
  onOffsetTooDeep(): void;
}

export class OutlineOffsetController {
  private drag: {
    source: OffsetCandidate;
    segment: { pathIndex: number; curveIndex: number };
    distance: number;
    wantInside: boolean;
    previewItem: paper.Item | null;
    /** Press point, to tell a real drag from a click. */
    start: { x: number; y: number };
    /** True once the pointer has left the press point by more than the slop.
     *  Until then the cursor sits exactly ON the source edge, where "which side
     *  am I on?" has no meaningful answer, so the side stays at its default. */
    moved: boolean;
  } | null = null;

  /** User-typed offset distance. When non-null the gesture commits at this
   *  magnitude regardless of how far the drag went — the cursor only picks the
   *  SIDE — so a click on an edge places an offset at exactly that distance.
   *  Mirrors `OffsetController.presetOffsetDistance` (#29) and the Stitch
   *  tool's Offset seam kind (#768). */
  private presetDistance: number | null = null;
  /** Rounded-corner preference for the next outline offset (#423) — the tool
   *  side of the same choice the construction Offset sub-tool carries. Feeds
   *  the preview and the commit from one field so the dashed outline can't
   *  promise corners the committed one won't have. */
  private roundedCorners = true;
  /** Fired per frame during a drag so the properties panel can mirror the live
   *  distance in its input. Inert while a preset is typed, since then the input
   *  is the source of truth rather than a readout. */
  private liveDistanceCallback?: (mm: number) => void;

  constructor(private host: OutlineOffsetHost) {}

  setPresetDistance(mm: number | null): void {
    this.presetDistance = mm;
  }

  setRoundedCorners(on: boolean): void {
    this.roundedCorners = on;
  }

  setLiveDistanceCallback(fn: ((mm: number) => void) | undefined): void {
    this.liveDistanceCallback = fn;
  }

  /** True while a press has engaged the gesture, so the owning tool knows to
   *  keep routing move / up here instead of its own drawing branches. */
  get active(): boolean {
    return this.drag !== null;
  }

  // -------------------------------------------------------------------
  // Hit-testing
  // -------------------------------------------------------------------

  private tolMm(): number {
    return (
      hitRadiusFor(EDGE_TOL_PX, currentPointerKind()) /
      (this.host.getScope().view?.zoom || 1)
    );
  }

  /** The nearest offsettable edge under `p`, or null. */
  private hitEdge(p: { x: number; y: number }): {
    source: OffsetCandidate;
    segment: { pathIndex: number; curveIndex: number };
  } | null {
    const scope = this.host.getScope();
    const tol = this.tolMm();
    let best: {
      source: OffsetCandidate;
      segment: { pathIndex: number; curveIndex: number };
    } | null = null;
    let bestDist = Infinity;
    for (const source of this.host.offsetCandidates()) {
      const edge = nearestOutlineEdge(source.pathData, p, scope, tol);
      if (!edge || edge.dist >= bestDist) continue;
      bestDist = edge.dist;
      best = {
        source,
        segment: { pathIndex: edge.pathIndex, curveIndex: edge.curveIndex },
      };
    }
    return best;
  }

  /** Which side the offset goes to for a cursor at `p`. A source that allows
   *  only one side clamps to it, so dragging the "wrong" way still previews
   *  the legal result instead of dead-ending (the inward-only rule the Stitch
   *  offset kind uses). */
  private sideFor(
    source: OffsetCandidate,
    p: { x: number; y: number },
  ): boolean {
    if (!source.allowOutside) return true;
    if (!source.allowInside) return false;
    return this.cursorIsInside(source.pathData, p);
  }

  private cursorIsInside(
    pathData: string,
    p: { x: number; y: number },
  ): boolean {
    const scope = this.host.getScope();
    try {
      const cp = new scope.CompoundPath({ pathData, insert: false });
      const inside = cp.contains(new scope.Point(p.x, p.y));
      cp.remove();
      return inside;
    } catch {
      return false;
    }
  }

  // -------------------------------------------------------------------
  // Pointer flow — the owning tool delegates when its kind is "offset"
  // -------------------------------------------------------------------

  /** Pre-press hover: the construction / stitch offset cursor treatment — a
   *  plain pointer plus the perpendicular-arrow affordance over an offsettable
   *  edge. Returns true when an edge is under the cursor. */
  handleHover(p: { x: number; y: number }): boolean {
    const scope = this.host.getScope();
    setCanvasCursor(scope, "default");
    const hit = this.hitEdge(p);
    if (!hit) {
      this.host.getScene().setOffsetHoverAffordance(null);
      return false;
    }
    const geo = offsetHoverGeometry(
      hit.source.pathData,
      hit.segment,
      p,
      scope,
      this.sideFor(hit.source, p),
    );
    this.host
      .getScene()
      .setOffsetHoverAffordance(geo ? { x: p.x, y: p.y, ...geo } : null);
    return true;
  }

  /** Engage on press. Returns true when an edge was grabbed, so the tool can
   *  suppress its own drawing branches. */
  handleDown(p: { x: number; y: number }): boolean {
    this.cancel();
    const hit = this.hitEdge(p);
    if (!hit) return false;
    this.drag = {
      source: hit.source,
      segment: hit.segment,
      distance: 0,
      // Default to the inset when it's legal. A press lands exactly on the
      // edge, so the cursor can't yet say which side is wanted; insetting is
      // the safer, more common intent (and the only legal one for a cutout off
      // a part outline). Any real movement overrides this immediately.
      wantInside: hit.source.allowInside,
      previewItem: null,
      start: { x: p.x, y: p.y },
      moved: false,
    };
    // The drag owns the preview + ray now — drop the hover arrow.
    this.host.getScene().setOffsetHoverAffordance(null);
    this.refreshPreview(p);
    return true;
  }

  handleMove(p: { x: number; y: number }): boolean {
    if (!this.drag) return false;
    this.refreshPreview(p);
    return true;
  }

  /** Commit on release. Returns true when the gesture consumed the release. */
  handleUp(): boolean {
    const drag = this.drag;
    if (!drag) return false;
    if (drag.previewItem) drag.previewItem.remove();
    this.drag = null;
    this.host.getScene().clearGuideRays();
    // The minimum exists to reject an accidental click that never became a
    // drag. A TYPED distance is not an accident — it's an explicit instruction,
    // and a fine one (a 0.25mm seam allowance is a real thing) — so the
    // threshold only guards the drag-defined path.
    const typed = this.presetDistance;
    const hasPreset = typed !== null && isFinite(typed) && typed > 0;
    if (!hasPreset && drag.distance < MIN_DISTANCE_MM) return true;
    const pathData = offsetOutlinePathData(
      drag.source.pathData,
      drag.distance,
      drag.wantInside,
      this.host.getScope(),
      { roundedCorners: this.roundedCorners },
    );
    // A null here is the honest "offset too deep" outcome — the engine answers
    // an over-deep inset with a fold-back ring, which `offsetOutlinePathData`
    // rejects rather than committing a corrupt outline.
    if (pathData) this.host.commitOffset(drag.source, pathData);
    else this.host.onOffsetTooDeep();
    return true;
  }

  /** Drop any in-flight drag and its preview, and clear the hover glyph. */
  cancel(): void {
    if (this.drag?.previewItem) this.drag.previewItem.remove();
    this.drag = null;
    this.host.getScene().setOffsetHoverAffordance(null);
    this.host.getScene().clearGuideRays();
  }

  // -------------------------------------------------------------------
  // Preview
  // -------------------------------------------------------------------

  private refreshPreview(cursor: { x: number; y: number }): void {
    const drag = this.drag;
    if (!drag) return;
    const scope = this.host.getScope();
    if (drag.previewItem) {
      drag.previewItem.remove();
      drag.previewItem = null;
    }
    const magnitude = distanceToEdge(
      drag.source.pathData,
      drag.segment,
      cursor,
      scope,
    );
    const foot = nearestPointOnEdge(
      drag.source.pathData,
      drag.segment,
      cursor,
      scope,
    );
    if (magnitude == null || !foot) return;
    if (
      !drag.moved &&
      Math.hypot(cursor.x - drag.start.x, cursor.y - drag.start.y) >
        SIDE_SLOP_MM
    )
      drag.moved = true;
    if (drag.moved) drag.wantInside = this.sideFor(drag.source, cursor);
    // With a typed preset the magnitude is fixed and the cursor contributes
    // only the side; without one the drag defines it, and we mirror the value
    // back into the panel so the user can read it live.
    const preset = this.presetDistance;
    if (preset !== null && isFinite(preset) && preset > 0) {
      drag.distance = preset;
    } else {
      drag.distance = magnitude;
      this.liveDistanceCallback?.(magnitude);
    }

    // Which side of the leather this gesture lands on — shared by the ray and
    // the dashed preview below, which are one affordance and must not disagree
    // about what they are drawn over.
    let overLeather = false;
    if (magnitude > 1e-6) {
      // Draw the ray at the distance that will actually COMMIT (the preset when
      // one is typed), not the raw cursor distance — the ray is a readout of
      // the result, so the two must never disagree.
      const shown = drag.distance;
      const ux = (cursor.x - foot.x) / magnitude;
      const uy = (cursor.y - foot.y) / magnitude;
      const to = { x: foot.x + ux * shown, y: foot.y + uy * shown };
      // The perpendicular foot sits exactly ON the source edge, where "over
      // leather?" is ambiguous and would answer for the wrong side. Ask about
      // the MIDPOINT of the span the offset covers — squarely in the region
      // both the ray and the preview outline occupy (#972). Offsetting into a
      // cutout in Leather mode therefore takes the mat accent, because the part
      // renders minus its voids there and the mat really does show through; in
      // Cutout mode the same spot keeps the leather accent, since that view
      // paints cutouts as markers over full leather.
      const mid = { x: (foot.x + to.x) / 2, y: (foot.y + to.y) / 2 };
      overLeather = this.host.getScene().isPointOverLeather(mid);
      this.host.getScene().setGuideRays([
        {
          from: { x: foot.x, y: foot.y },
          to,
          lengthMm: shown,
          overLeather,
        },
      ]);
    }

    const previewData = offsetOutlinePathData(
      drag.source.pathData,
      drag.distance,
      drag.wantInside,
      scope,
      { roundedCorners: this.roundedCorners },
    );
    if (!previewData) return;
    try {
      const preview = new scope.CompoundPath({
        pathData: previewData,
        insert: true,
      });
      // An inset from a part edge lies ON the leather and takes the dark accent;
      // an outset lands on the bare mat and keeps the light one.
      preview.strokeColor = new scope.Color(
        overLeather ? PREVIEW_STROKE_OVER_LEATHER() : PREVIEW_STROKE(),
      );
      preview.strokeWidth = 1.6;
      preview.strokeScaling = false;
      preview.dashArray = [4, 3];
      preview.fillColor = null;
      preview.data = { ephemeral: true };
      drag.previewItem = preview;
    } catch {
      /* preview only */
    }
  }
}
